// ═══════════════════════════════════════════════════════════════
// AWARDS TAB — Record and manage scholarship disbursements
// Path: src/components/scholarships/AwardsTab.jsx
// ═══════════════════════════════════════════════════════════════

import React, { useState, useEffect, useCallback } from "react";
import { Plus, Trash2, Award } from "lucide-react";
import { useApp } from "../../context/AppContext";
import { FONT_SIZES, FONT_FAMILY, ICON_SIZES } from "../../constants/appConfig";
import { ActionButton } from "../ui/ActionButton";
import {
  scholarshipsService,
  scholarshipAwardsService,
  getCurrentSeason,
  getSeasonOptions,
} from "../../services/scholarshipService";

const fmtCurrency = (amount) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
  }).format(amount || 0);

const fmtDate = (dateStr) => {
  if (!dateStr) return "—";
  return new Date(dateStr + "T12:00:00").toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const emptyForm = (season) => ({
  scholarshipId: "",
  studentName: "",
  studentEmail: "",
  awardAmount: "",
  season,
  awardDate: new Date().toISOString().slice(0, 10),
  notes: "",
});

export const AwardsTab = () => {
  const { palette, showMessage, showLoading, hideLoading } = useApp();

  const [season, setSeason] = useState(getCurrentSeason());
  const [awards, setAwards] = useState([]);
  const [scholarships, setScholarships] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyForm(getCurrentSeason()));

  const seasonOptions = getSeasonOptions();

  // ── Load ────────────────────────────────────────────────────
  const loadAwards = useCallback(async () => {
    showLoading("Loading awards...");
    try {
      const data = await scholarshipAwardsService.getBySeason(season);
      setAwards(data);
    } catch (err) {
      // silent
    } finally {
      hideLoading();
    }
  }, [season, showLoading, hideLoading]);

  useEffect(() => {
    loadAwards();
  }, [loadAwards]);

  useEffect(() => {
    scholarshipsService.getForDropdown().then(setScholarships).catch(() => {});
  }, []);

  // ── Create ──────────────────────────────────────────────────
  const handleCreate = async () => {
    if (!form.scholarshipId || !form.studentName.trim() || !form.awardAmount) return;
    try {
      await scholarshipAwardsService.create({
        ...form,
        awardAmount: parseFloat(form.awardAmount),
      });
      setForm(emptyForm(season));
      setShowForm(false);
      loadAwards();
      showMessage("Award recorded");
    } catch (err) {
      showMessage("Error recording award", "error");
    }
  };

  // ── Delete ──────────────────────────────────────────────────
  const handleDelete = async (awardId) => {
    try {
      await scholarshipAwardsService.remove(awardId);
      loadAwards();
      showMessage("Award removed", "warning");
    } catch (err) {
      showMessage("Error removing award", "error");
    }
  };

  // ── Totals ──────────────────────────────────────────────────
  const totalDisbursed = awards.reduce((s, a) => s + (Number(a.award_amount) || 0), 0);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      {/* Controls */}
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <label style={{ fontSize: FONT_SIZES.xs, fontWeight: 600, color: palette.text2, fontFamily: FONT_FAMILY }}>
          Season:
        </label>
        <select
          value={season}
          onChange={(e) => setSeason(e.target.value)}
          style={{
            padding: "6px 10px", borderRadius: 6,
            fontSize: FONT_SIZES.xs, fontFamily: FONT_FAMILY,
            border: `1px solid ${palette.inputBorder}`, background: palette.inputBg, color: palette.inputColor, outline: "none",
          }}
        >
          {seasonOptions.map((s) => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>
        <div style={{ flex: 1 }} />
        <span style={{ fontSize: FONT_SIZES.xs, fontWeight: 600, color: palette.text2, fontFamily: FONT_FAMILY }}>
          {awards.length} awards · {fmtCurrency(totalDisbursed)} disbursed
        </span>
        <ActionButton
          label="New Award"
          icon={Plus}
          small
          onClick={() => { setForm(emptyForm(season)); setShowForm(true); }}
        />
      </div>

      {/* New Award Form */}
      {showForm && (
        <div
          style={{
            background: palette.cardBg,
            border: `1px solid ${palette.cardBorder}`,
            borderRadius: 10,
            padding: 20,
          }}
        >
          <div style={{ fontSize: FONT_SIZES.xs, fontWeight: 700, color: palette.text1, fontFamily: FONT_FAMILY, marginBottom: 12 }}>
            Record Award
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <div>
              <label style={{ fontSize: "0.6rem", fontWeight: 600, color: palette.text2, fontFamily: FONT_FAMILY, display: "block", marginBottom: 4 }}>
                Scholarship *
              </label>
              <select
                value={form.scholarshipId}
                onChange={(e) => setForm((f) => ({ ...f, scholarshipId: e.target.value }))}
                style={{
                  width: "100%", padding: "7px 10px", borderRadius: 6,
                  fontSize: FONT_SIZES.xs, fontFamily: FONT_FAMILY,
                  border: `1px solid ${palette.inputBorder}`, background: palette.inputBg, color: palette.inputColor, outline: "none",
                }}
              >
                <option value="">Select scholarship...</option>
                {scholarships.map((s) => (
                  <option key={s.id} value={s.id}>{s.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label style={{ fontSize: "0.6rem", fontWeight: 600, color: palette.text2, fontFamily: FONT_FAMILY, display: "block", marginBottom: 4 }}>
                Student Name *
              </label>
              <input
                value={form.studentName}
                onChange={(e) => setForm((f) => ({ ...f, studentName: e.target.value }))}
                style={{
                  width: "100%", padding: "7px 10px", borderRadius: 6,
                  fontSize: FONT_SIZES.xs, fontFamily: FONT_FAMILY,
                  border: `1px solid ${palette.inputBorder}`, background: palette.inputBg, color: palette.inputColor, outline: "none",
                }}
              />
            </div>
            <div>
              <label style={{ fontSize: "0.6rem", fontWeight: 600, color: palette.text2, fontFamily: FONT_FAMILY, display: "block", marginBottom: 4 }}>
                Amount *
              </label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={form.awardAmount}
                onChange={(e) => setForm((f) => ({ ...f, awardAmount: e.target.value }))}
                style={{
                  width: "100%", padding: "7px 10px", borderRadius: 6,
                  fontSize: FONT_SIZES.xs, fontFamily: FONT_FAMILY,
                  border: `1px solid ${palette.inputBorder}`, background: palette.inputBg, color: palette.inputColor, outline: "none",
                }}
              />
            </div>
            <div>
              <label style={{ fontSize: "0.6rem", fontWeight: 600, color: palette.text2, fontFamily: FONT_FAMILY, display: "block", marginBottom: 4 }}>
                Award Date
              </label>
              <input
                type="date"
                value={form.awardDate}
                onChange={(e) => setForm((f) => ({ ...f, awardDate: e.target.value }))}
                style={{
                  width: "100%", padding: "7px 10px", borderRadius: 6,
                  fontSize: FONT_SIZES.xs, fontFamily: FONT_FAMILY,
                  border: `1px solid ${palette.inputBorder}`, background: palette.inputBg, color: palette.inputColor, outline: "none",
                }}
              />
            </div>
            <div>
              <label style={{ fontSize: "0.6rem", fontWeight: 600, color: palette.text2, fontFamily: FONT_FAMILY, display: "block", marginBottom: 4 }}>
                Student Email
              </label>
              <input
                value={form.studentEmail}
                onChange={(e) => setForm((f) => ({ ...f, studentEmail: e.target.value }))}
                style={{
                  width: "100%", padding: "7px 10px", borderRadius: 6,
                  fontSize: FONT_SIZES.xs, fontFamily: FONT_FAMILY,
                  border: `1px solid ${palette.inputBorder}`, background: palette.inputBg, color: palette.inputColor, outline: "none",
                }}
              />
            </div>
            <div>
              <label style={{ fontSize: "0.6rem", fontWeight: 600, color: palette.text2, fontFamily: FONT_FAMILY, display: "block", marginBottom: 4 }}>
                Notes
              </label>
              <input
                value={form.notes}
                onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
                style={{
                  width: "100%", padding: "7px 10px", borderRadius: 6,
                  fontSize: FONT_SIZES.xs, fontFamily: FONT_FAMILY,
                  border: `1px solid ${palette.inputBorder}`, background: palette.inputBg, color: palette.inputColor, outline: "none",
                }}
              />
            </div>
          </div>
          <div style={{ display: "flex", gap: 8, marginTop: 14 }}>
            <ActionButton label="Cancel" variant="secondary" small onClick={() => setShowForm(false)} />
            <ActionButton
              label="Record Award"
              icon={Award}
              small
              onClick={handleCreate}
              disabled={!form.scholarshipId || !form.studentName.trim() || !form.awardAmount}
            />
          </div>
        </div>
      )}

      {/* Awards List */}
      <div
        style={{
          background: palette.cardBg,
          border: `1px solid ${palette.cardBorder}`,
          borderRadius: 10,
          overflow: "hidden",
        }}
      >
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "2fr 2fr 1fr 1fr auto",
            gap: 8,
            padding: "10px 16px",
            borderBottom: `1px solid ${palette.cardBorder}`,
            fontSize: "0.6rem",
            fontWeight: 600,
            color: palette.text3,
            fontFamily: FONT_FAMILY,
          }}
        >
          <span>Scholarship</span>
          <span>Student</span>
          <span>Amount</span>
          <span>Date</span>
          <span></span>
        </div>

        {awards.length === 0 ? (
          <div style={{ padding: 24, textAlign: "center", fontSize: FONT_SIZES.xs, color: palette.text3, fontFamily: FONT_FAMILY }}>
            No awards in {season}.
          </div>
        ) : (
          awards.map((a) => (
            <div
              key={a.id}
              style={{
                display: "grid",
                gridTemplateColumns: "2fr 2fr 1fr 1fr auto",
                gap: 8,
                padding: "8px 16px",
                borderBottom: `1px solid ${palette.cardBorder}`,
                alignItems: "center",
              }}
            >
              <span style={{ fontSize: FONT_SIZES.xs, fontWeight: 600, color: palette.text1, fontFamily: FONT_FAMILY }}>
                {a.scholarships?.name || "—"}
              </span>
              <div>
                <div style={{ fontSize: FONT_SIZES.xs, color: palette.text1, fontFamily: FONT_FAMILY }}>
                  {a.student_name}
                </div>
                {a.student_email && (
                  <div style={{ fontSize: "0.55rem", color: palette.text3, fontFamily: FONT_FAMILY }}>
                    {a.student_email}
                  </div>
                )}
              </div>
              <span style={{ fontSize: FONT_SIZES.xs, fontWeight: 600, color: palette.accent1 || "#3b82f6", fontFamily: FONT_FAMILY }}>
                {fmtCurrency(a.award_amount)}
              </span>
              <span style={{ fontSize: "0.6rem", color: palette.text3, fontFamily: FONT_FAMILY }}>
                {fmtDate(a.award_date)}
              </span>
              <button
                onClick={() => handleDelete(a.id)}
                style={{ background: "none", border: "none", cursor: "pointer", padding: 2, display: "flex", opacity: 0.4 }}
                onMouseEnter={(e) => { e.currentTarget.style.opacity = "1"; }}
                onMouseLeave={(e) => { e.currentTarget.style.opacity = "0.4"; }}
              >
                <Trash2 size={ICON_SIZES.sm} style={{ color: palette.text3 }} />
              </button>
            </div>
          ))
        )}
      </div>
    </div>
  );
};
